<template>
  <section
    v-if="hasSection"
    id="prices"
    class="lpu-prices d-flex flex-column"
    :class="isMobile ? 'ga-4' : 'ga-6'"
    data-qa="lpu_prices"
  >
    <div
      class="d-flex flex-column"
      :class="{ 'ga-4': appointmentPhone }"
    >
      <h2
        class="text--text"
        :class="isMobile ? 'text-h6' : 'text-h5'"
        data-qa="lpu_prices_title"
      >
        {{ tStatic('Цены') }}
      </h2>
      <div
        v-if="appointmentPhone"
        class="d-flex align-center ga-3"
      >
        <span class="text-body-1 text-secondary--text">
          {{ tStatic('Запись на приём:') }}
        </span>
        <span class="d-flex align-center ga-2">
          <span
            class="ui-icon-ui-call ui-icon-fz-smaller flex-shrink-0 text-lpu-branding-color-primary"
            aria-hidden="true"
          />
          <span class="text-body-1 font-weight-medium text--text">
            {{ appointmentPhone }}
          </span>
        </span>
      </div>
    </div>

    <PricesServiceList
      v-if="hasSynonymPrices"
      :services="services"
      :show-all-href="synonymShowAllHref"
      :lpu-id="lpu.id"
      :town-timedelta="townTimedelta"
      @empty-schedule="onEmptySchedule"
      @slot="onSlotClick"
    />
    <PricesRangeList
      v-else
      :items="ranges"
      :prices-path="pricesPath"
      :show-all-href="rangeShowAllHref"
    />

    <InvasiveServiceInfo v-if="isClientMounted && hasSynonymPrices" />
  </section>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import InvasiveServiceInfo from 'src/components/common/InvasiveServiceInfo/InvasiveServiceInfo.vue'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import type { ContextStore } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/index/types'
import type { Lpu } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import formatPhone from 'src/functions/formatPhone'
import getLpuPhone from 'src/utils/getLpuPhone'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import PricesRangeList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/components/PricesRangeList.vue'
import PricesServiceList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/components/PricesServiceList.vue'
import useServiceAppointment from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/composables/useServiceAppointment'
import {
  RANGE_MOBILE_MORE_THRESHOLD,
  SYNONYM_SHOW_ALL_THRESHOLD,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPrices/constants'
import unwrapList from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/unwrapList'
import getTownTimedelta from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/getTownTimedelta'

const props = defineProps<{
  lpu: Lpu
}>()

const isMobile = useIsMobile()
const { context }: ContextStore = useContextStore()
const { reverseFull } = useURLStore()
const { onEmptySchedule, onSlotClick } = useServiceAppointment({
  lpuId: props.lpu.id,
  townTranslit: props.lpu.town.translit,
  isEnabledDirect: Boolean(props.lpu.appointment_service_direct),
})

const isClientMounted = ref(false)
const townTimedelta = computed(() => getTownTimedelta([
  context.town.timedelta,
  props.lpu.town.timedelta,
]))
const services = computed(() => unwrapList(context.synonym_prices))
const ranges = computed(() => unwrapList(context.prices_min_max))
const hasSynonymPrices = computed(() => (context.synonym_prices_count || 0) > 0)
const hasSection = computed(() => hasSynonymPrices.value || ranges.value.length > 0)
const pricesPath = reverseFull({
  name: 'lpu_price',
  args: [
    props.lpu.town.translit,
    props.lpu.translit,
  ],
})
const synonymShowAllHref = computed(() => (
  (context.synonym_prices_count || 0) > SYNONYM_SHOW_ALL_THRESHOLD
    ? pricesPath
    : undefined
))
const rangeShowAllHref = computed(() => {
  if (isMobile.value && ranges.value.length <= RANGE_MOBILE_MORE_THRESHOLD) {
    return undefined
  }

  return isMobile.value
    ? pricesPath
    : `${pricesPath}#prices`
})
const appointmentPhoneRaw = computed(() => getLpuPhone({
  lpu: props.lpu,
  lpuAddress: props.lpu.addr?.[0],
}) || '')
const appointmentPhone = computed(() => (
  !isMobile.value && appointmentPhoneRaw.value
    ? formatPhone({
      phone: appointmentPhoneRaw.value,
      country: context.request.country,
    })
    : ''
))

onMounted(() => {
  isClientMounted.value = true
})
</script>
